import pick from 'lodash/pick';
import {
  createShippingOrderWithShippo,
  createShippingOrderDeferredWithShippo,
  fetchShippingMethodsWithRatesFromShippo,
  initiatePrivileged,
  transitionPrivileged,
} from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { fetchCurrentUserHasOrdersSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import { isPrivileged as isPurchasePrivileged } from '../../transactions/transactionProcessPurchase';
import { isPrivileged as isDeferredPurchasePrivileged } from '../../transactions/transactionProcessDeferredPurchase';
import { isDeferredPurchaseProcessAlias, DEFERRED_PURCHASE_PROCESS_NAME } from '../../transactions/transaction';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/CheckoutPage/SET_INITIAL_VALUES';

export const INITIATE_ORDER_REQUEST = 'app/CheckoutPage/INITIATE_ORDER_REQUEST';
export const INITIATE_ORDER_SUCCESS = 'app/CheckoutPage/INITIATE_ORDER_SUCCESS';
export const INITIATE_ORDER_ERROR = 'app/CheckoutPage/INITIATE_ORDER_ERROR';

export const CONFIRM_PAYMENT_REQUEST = 'app/CheckoutPage/CONFIRM_PAYMENT_REQUEST';
export const CONFIRM_PAYMENT_SUCCESS = 'app/CheckoutPage/CONFIRM_PAYMENT_SUCCESS';
export const CONFIRM_PAYMENT_ERROR = 'app/CheckoutPage/CONFIRM_PAYMENT_ERROR';

export const SPECULATE_TRANSACTION_REQUEST = 'app/CheckoutPage/SPECULATE_TRANSACTION_REQUEST';
export const SPECULATE_TRANSACTION_SUCCESS = 'app/CheckoutPage/SPECULATE_TRANSACTION_SUCCESS';
export const SPECULATE_TRANSACTION_ERROR = 'app/CheckoutPage/SPECULATE_TRANSACTION_ERROR';

export const STRIPE_CUSTOMER_REQUEST = 'app/CheckoutPage/STRIPE_CUSTOMER_REQUEST';
export const STRIPE_CUSTOMER_SUCCESS = 'app/CheckoutPage/STRIPE_CUSTOMER_SUCCESS';
export const STRIPE_CUSTOMER_ERROR = 'app/CheckoutPage/STRIPE_CUSTOMER_ERROR';

export const INITIATE_INQUIRY_REQUEST = 'app/CheckoutPage/INITIATE_INQUIRY_REQUEST';
export const INITIATE_INQUIRY_SUCCESS = 'app/CheckoutPage/INITIATE_INQUIRY_SUCCESS';
export const INITIATE_INQUIRY_ERROR = 'app/CheckoutPage/INITIATE_INQUIRY_ERROR';

export const FETCH_SHIPPING_METHODS_REQUEST = 'app/CheckoutPage/FETCH_SHIPPING_METHODS_REQUEST';
export const FETCH_SHIPPING_METHODS_SUCCESS = 'app/CheckoutPage/FETCH_SHIPPING_METHODS_SUCCESS';
export const FETCH_SHIPPING_METHODS_ERROR = 'app/CheckoutPage/FETCH_SHIPPING_METHODS_ERROR';

export const UPDATING_SHIPPING_PRICE = 'app/CheckoutPage/UPDATING_SHIPPING_PRICE';
export const IS_TOTAL_LISTING_METHODS_LENGTH_ZERO =
  'app/CheckoutPage/IS_TOTAL_LISTING_METHODS_LENGTH_ZERO';

// ================ Reducer ================ //

const initialState = {
  listing: null,
  orderData: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  isClockInSync: false,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
  initiateInquiryInProgress: false,
  initiateInquiryError: null,
  updatingShippingPriceInProgress: false,
  fetchShippingMethodsInProgress: false,
  shippingMethods: [],
  fetchShippingMethodsError: null,
  noShippingProvider: null,
  isShipingMethodsLengthZero: false,
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SPECULATE_TRANSACTION_REQUEST:
      return {
        ...state,
        speculateTransactionInProgress: true,
        speculateTransactionError: null,
        speculatedTransaction: null,
      };
    case SPECULATE_TRANSACTION_SUCCESS: {
      // Check that the local devices clock is within a minute from the server
      const lastTransitionedAt = payload.transaction?.attributes?.lastTransitionedAt;
      const localTime = new Date();
      const minute = 60000;
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculatedTransaction: payload.transaction,
        isClockInSync: Math.abs(lastTransitionedAt?.getTime() - localTime.getTime()) < minute,
      };
    }
    case SPECULATE_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculateTransactionError: payload,
      };

    case INITIATE_ORDER_REQUEST:
      return { ...state, initiateOrderError: null };
    case INITIATE_ORDER_SUCCESS:
      return { ...state, transaction: payload };
    case INITIATE_ORDER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, initiateOrderError: payload };

    case CONFIRM_PAYMENT_REQUEST:
      return { ...state, confirmPaymentError: null };
    case CONFIRM_PAYMENT_SUCCESS:
      return state;
    case CONFIRM_PAYMENT_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, confirmPaymentError: payload };

    case STRIPE_CUSTOMER_REQUEST:
      return { ...state, stripeCustomerFetched: false };
    case STRIPE_CUSTOMER_SUCCESS:
      return { ...state, stripeCustomerFetched: true };
    case STRIPE_CUSTOMER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, stripeCustomerFetchError: payload };

    case INITIATE_INQUIRY_REQUEST:
      return { ...state, initiateInquiryInProgress: true, initiateInquiryError: null };
    case INITIATE_INQUIRY_SUCCESS:
      return { ...state, initiateInquiryInProgress: false };
    case INITIATE_INQUIRY_ERROR:
      return { ...state, initiateInquiryInProgress: false, initiateInquiryError: payload };

    // ----------------shipping methods start---------------

    case FETCH_SHIPPING_METHODS_REQUEST:
      return {
        ...state,
        fetchShippingMethodsInProgress: true,
        fetchShippingMethodsError: null,
        shippingMethods: [],
        noShippingProvider: null,
      };
    case FETCH_SHIPPING_METHODS_SUCCESS:
      return {
        ...state,
        fetchShippingMethodsInProgress: false,
        shippingMethods: payload,
        noShippingProvider: null,
        isShipingMethodsLengthZero: payload.length == '0' ? true : false,
      };
    case UPDATING_SHIPPING_PRICE:
      return { ...state, updatingShippingPriceInProgress: payload };

    case FETCH_SHIPPING_METHODS_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        fetchShippingMethodsInProgress: false,
        fetchShippingMethodsError: payload,
        noShippingProvider: null,
      };

    // ---------------------------shipingmethods end-------------------------------
    case IS_TOTAL_LISTING_METHODS_LENGTH_ZERO:
      return {
        ...state,
        isShipingMethodsLengthZero: false,
      };
    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //
export const setIsShipingMethodsLengthZero = () => ({
  type: IS_TOTAL_LISTING_METHODS_LENGTH_ZERO,
});

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const initiateOrderRequest = () => ({ type: INITIATE_ORDER_REQUEST });

const initiateOrderSuccess = order => ({
  type: INITIATE_ORDER_SUCCESS,
  payload: order,
});

const initiateOrderError = e => ({
  type: INITIATE_ORDER_ERROR,
  error: true,
  payload: e,
});

const confirmPaymentRequest = () => ({ type: CONFIRM_PAYMENT_REQUEST });

const confirmPaymentSuccess = orderId => ({
  type: CONFIRM_PAYMENT_SUCCESS,
  payload: orderId,
});

const confirmPaymentError = e => ({
  type: CONFIRM_PAYMENT_ERROR,
  error: true,
  payload: e,
});

export const speculateTransactionRequest = () => ({ type: SPECULATE_TRANSACTION_REQUEST });

export const speculateTransactionSuccess = transaction => ({
  type: SPECULATE_TRANSACTION_SUCCESS,
  payload: { transaction },
});

export const speculateTransactionError = e => ({
  type: SPECULATE_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

export const stripeCustomerRequest = () => ({ type: STRIPE_CUSTOMER_REQUEST });
export const stripeCustomerSuccess = () => ({ type: STRIPE_CUSTOMER_SUCCESS });
export const stripeCustomerError = e => ({
  type: STRIPE_CUSTOMER_ERROR,
  error: true,
  payload: e,
});

export const initiateInquiryRequest = () => ({ type: INITIATE_INQUIRY_REQUEST });
export const initiateInquirySuccess = () => ({ type: INITIATE_INQUIRY_SUCCESS });
export const initiateInquiryError = e => ({
  type: INITIATE_INQUIRY_ERROR,
  error: true,
  payload: e,
});

export const fetchShippingMethodsRequest = () => ({ type: FETCH_SHIPPING_METHODS_REQUEST });
export const fetchShippingMethodsSuccess = payload => {
  console.log('payloaddddddddddd', payload);
  return {
    type: FETCH_SHIPPING_METHODS_SUCCESS,
    payload,
  };
};
export const fetchShippingMethodsError = e => ({
  type: FETCH_SHIPPING_METHODS_ERROR,
  error: true,
  payload: e,
});

export const updatingShippingPrice = payload => ({ type: UPDATING_SHIPPING_PRICE, payload });
/* ================ Thunks ================ */

export const initiateOrder = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  dispatch(initiateOrderRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;

  const { deliveryMethod, shipment_id, quantity, bookingDates, ...otherOrderParams } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  // Include shipment_id in orderData so the server can calculate shipping price
  const shipmentIdMaybe = shipment_id ? { shipment_id } : {};

  // Parameters only for client app's server
  const orderData = {
    ...(deliveryMethod && { deliveryMethod }),
    ...shipmentIdMaybe,
  };

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
  };

  const bodyParams = isTransition
    ? {
      id: transactionId,
      transition: transitionName,
      params: transitionParams,
    }
    : {
      processAlias,
      transition: transitionName,
      params: transitionParams,
    };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSucces = response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    dispatch(initiateOrderSuccess(order));
    dispatch(fetchCurrentUserHasOrdersSuccess(true));
    return order;
  };

  const handleError = e => {
    dispatch(initiateOrderError(storableError(e)));
    const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
    log.error(e, 'initiate-order-failed', {
      ...transactionIdMaybe,
      listingId: orderParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    throw e;
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSucces)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transition(bodyParams, queryParams)
      .then(handleSucces)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSucces)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiate(bodyParams, queryParams)
      .then(handleSucces)
      .catch(handleError);
  }
};

export const confirmPayment = (transactionId, transitionName, transitionParams = {}, isDeferredPurchaseHint = false) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(confirmPaymentRequest());

  console.log('[confirmPayment] Starting with isDeferredPurchaseHint:', isDeferredPurchaseHint);

  // ALWAYS fetch the transaction first to reliably detect if it's deferred-purchase
  // This ensures we don't rely on potentially stale client-side data
  return sdk.transactions.show({ id: transactionId })
    .then(response => {
      const tx = response.data.data;
      const processName = tx?.attributes?.processName;
      const isDeferredPurchase = processName === 'deferred-purchase';

      console.log('[confirmPayment] Fetched transaction - processName:', processName);
      console.log('[confirmPayment] isDeferredPurchase (server-verified):', isDeferredPurchase);
      console.log('[confirmPayment] isDeferredPurchaseHint (client-side):', isDeferredPurchaseHint);

      // For deferred-purchase, use the Shippo endpoint which:
      // 1. Creates the Shippo shipping label FIRST
      // 2. Makes transition/confirm-payment with shipping data in protectedData
      // This matches the pattern used by default-purchase with transition/accept
      if (isDeferredPurchase) {
        console.log('Deferred purchase: Creating Shippo order and confirming payment');
        return createShippingOrderDeferredWithShippo({ id: transactionId.uuid })
          .then(response => {
            console.log('Deferred purchase: Shippo order created and payment confirmed successfully');
            const order = response?.data?.data?.data || response?.data?.data;
            dispatch(confirmPaymentSuccess(order?.id || transactionId));
            return order;
          })
          .catch(e => {
            console.error('Deferred purchase: Failed to create Shippo order and confirm payment', e);
            dispatch(confirmPaymentError(storableError(e)));
            const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
            log.error(e, 'deferred-purchase-confirm-payment-failed', {
              ...transactionIdMaybe,
            });
            throw e;
          });
      }

      // For default-purchase, use the standard SDK transition
      const bodyParams = {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      };
      const queryParams = {
        include: ['booking', 'provider'],
        expand: true,
      };

      return sdk.transactions
        .transition(bodyParams, queryParams)
        .then(response => {
          const order = response.data.data;
          dispatch(confirmPaymentSuccess(order.id));
          return order;
        })
        .catch(e => {
          dispatch(confirmPaymentError(storableError(e)));
          const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
          log.error(e, 'initiate-order-failed', {
            ...transactionIdMaybe,
          });
          throw e;
        });
    })
    .catch(e => {
      // If transaction fetch fails, we can't proceed
      console.error('[confirmPayment] Failed to fetch transaction:', e);
      dispatch(confirmPaymentError(storableError(e)));
      throw e;
    });
};
export const speculateTransactionWithShippo = (orderParams, transactionId = null, processAlias = 'default-purchase/release-1') => async (
  dispatch,
  getState
) => {
  console.log('transation id>>>>>>>>>>>', transactionId);
  dispatch(updatingShippingPrice(true));
  const isTransition = !!transactionId;

  // Determine the correct transition based on process alias
  const isDeferredPurchase = isDeferredPurchaseProcessAlias(processAlias);
  const transition = isDeferredPurchase
    ? 'transition/request-order'
    : 'transition/request-payment';

  // Use the correct isPrivileged check based on process type
  const isPrivilegedTransition = isDeferredPurchase
    ? isDeferredPurchasePrivileged(transition)
    : isPurchasePrivileged(transition);

  const { deliveryMethod, shipment_id, quantity, bookingDates, ...otherOrderParams } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  const shipmentIdMaybe = shipment_id ? { shipment_id } : {};

  // Parameters only for client app's server
  const orderData = {
    deliveryMethod,
    ...shipmentIdMaybe,
  };

  // Parameters for Flex API
  // cardToken is only needed for payment-related transitions (not for deferred-purchase)
  const cardTokenMaybe = isDeferredPurchase ? {} : { cardToken: 'CheckoutPage_speculative_card_token' };
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    ...cardTokenMaybe,
  };

  const bodyParams = isTransition
    ? {
      id: transactionId,
      transition,
      params: transitionParams,
    }
    : {
      processAlias: processAlias,
      transition,
      params: transitionParams,
    };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    dispatch(speculateTransactionSuccess(tx));
    dispatch(updatingShippingPrice(false));
  };

  const handleError = e => {
    log.error(e, 'speculate-transaction-failed', {
      listingId: transitionParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    dispatch(updatingShippingPrice(false));
    return dispatch(speculateTransactionError(storableError(e)));
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transitionSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiateSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

export const sendMessage = params => (dispatch, getState, sdk) => {
  const message = params.message;
  const orderId = params.id;

  if (message) {
    return sdk.messages
      .send({ transactionId: orderId, content: message })
      .then(() => {
        return { orderId, messageSuccess: true };
      })
      .catch(e => {
        log.error(e, 'initial-message-send-failed', { txId: orderId });
        return { orderId, messageSuccess: false };
      });
  } else {
    return Promise.resolve({ orderId, messageSuccess: true });
  }
};

/**
 * Initiate transaction against default-inquiry process
 * Note: At this point inquiry transition is made directly against Marketplace API.
 *       So, client app's server is not involved here unlike with transitions including payments.
 *
 * @param {*} inquiryParams contains listingId and protectedData
 * @param {*} processAlias 'default-inquiry/release-1'
 * @param {*} transitionName 'transition/inquire-without-payment'
 * @returns
 */
export const initiateInquiryWithoutPayment = (inquiryParams, processAlias, transitionName) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(initiateInquiryRequest());

  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    dispatch(initiateInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  const bodyParams = {
    transition: transitionName,
    processAlias,
    params: inquiryParams,
  };
  const queryParams = {
    include: ['provider'],
    expand: true,
  };

  return sdk.transactions
    .initiate(bodyParams, queryParams)
    .then(response => {
      const transactionId = response.data.data.id;
      dispatch(initiateInquirySuccess());
      return transactionId;
    })
    .catch(e => {
      dispatch(initiateInquiryError(storableError(e)));
      throw e;
    });
};

/**
 * Initiate transaction against deferred-purchase process
 * This is used when the seller doesn't have Stripe connected.
 * The buyer expresses interest and the seller can accept/decline.
 * Payment happens later after the seller accepts.
 *
 * @param {*} interestParams contains listingId, quantity, and protectedData
 * @param {*} processAlias 'deferred-purchase/release-1'
 * @param {*} transitionName 'transition/request-interest'
 * @returns
 */
export const initiateInterestWithoutPayment = (interestParams, processAlias, transitionName) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(initiateInquiryRequest());

  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: interestParams?.listingId?.uuid,
    });
    dispatch(initiateInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  const bodyParams = {
    transition: transitionName,
    processAlias,
    params: interestParams,
  };
  const queryParams = {
    include: ['provider'],
    expand: true,
  };

  return sdk.transactions
    .initiate(bodyParams, queryParams)
    .then(response => {
      const transactionId = response.data.data.id;
      dispatch(initiateInquirySuccess());
      return transactionId;
    })
    .catch(e => {
      dispatch(initiateInquiryError(storableError(e)));
      throw e;
    });
};

/**
 * Initiate or transition the speculative transaction with the given
 * booking details
 *
 * The API allows us to do speculative transaction initiation and
 * transitions. This way we can create a test transaction and get the
 * actual pricing information as if the transaction had been started,
 * without affecting the actual data.
 *
 * We store this speculative transaction in the page store and use the
 * pricing info for the booking breakdown to get a proper estimate for
 * the price with the chosen information.
 */
export const speculateTransaction = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  dispatch(speculateTransactionRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;

  const { deliveryMethod, quantity, bookingDates, shipment_id, ...otherOrderParams } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  console.log('speculateTransaction - orderParams:', orderParams);
  console.log('speculateTransaction - shipment_id:', shipment_id);

  // Parameters only for client app's server
  // Include shipment_id for shipping cost calculation (especially for deferred payment completion)
  const orderData = {
    ...(deliveryMethod && { deliveryMethod }),
    ...(shipment_id && { shipment_id }),
  };

  console.log('speculateTransaction - orderData being sent:', orderData);

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = isTransition
    ? {
      id: transactionId,
      transition: transitionName,
      params: transitionParams,
    }
    : {
      processAlias,
      transition: transitionName,
      params: transitionParams,
    };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    dispatch(speculateTransactionSuccess(tx));
  };

  const handleError = e => {
    log.error(e, 'speculate-transaction-failed', {
      listingId: transitionParams.listingId.uuid,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    return dispatch(speculateTransactionError(storableError(e)));
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transitionSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiateSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

// StripeCustomer is a relantionship to currentUser
// We need to fetch currentUser with correct params to include relationship
export const stripeCustomer = () => (dispatch, getState, sdk) => {
  dispatch(stripeCustomerRequest());

  return dispatch(fetchCurrentUser({ include: ['stripeCustomer.defaultPaymentMethod'] }))
    .then(response => {
      dispatch(stripeCustomerSuccess());
    })
    .catch(e => {
      dispatch(stripeCustomerError(storableError(e)));
    });
};
export const fetchAllShippingMethods = (shippingAddress, transactionId) => (
  dispatch,
  getState,
  sdk
) => {
  console.log('transactionid', transactionId);
  dispatch(fetchShippingMethodsRequest());
  // dispatch(resetShippingDetailsChanged());
  fetchShippingMethodsWithRatesFromShippo(shippingAddress)
    .then(res => {
      dispatch(fetchShippingMethodsSuccess(res));
    })
    .catch(err => {
      const error = err?.response?.data?.message ?? err?.message ?? 'Unknown error';
      dispatch(fetchShippingMethodsError(error));
    });
};
